import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminChrome from '@/components/AdminChrome';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, Search, Users, Crown, GraduationCap, Briefcase, Mail,
  Calendar, ShieldCheck, AlertTriangle, Sparkles, X, MessageSquare, ArrowLeft,
  Activity, Wifi, Clock, UserCog
} from 'lucide-react';

const initials = (name) => (name || '?').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

const AdminTeam = () => {
  const { api, user, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, admins: 0, interns: 0, staff: 0 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | admins | interns | staff
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [view, setView] = useState('members'); // members | activity
  const [activity, setActivity] = useState({ online_now: [], events: [], online_count: 0 });
  const [activityLoading, setActivityLoading] = useState(false);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/team${q ? `?search=${encodeURIComponent(q)}` : ''}`);
      setMembers(res.data.members || []);
      setStats(res.data.stats || { total: 0, admins: 0, interns: 0, staff: 0 });
    } catch (err) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const res = await api.get('/admin/staff/activity?minutes=240&limit=100');
      setActivity(res.data || { online_now: [], events: [] });
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (view !== 'activity') return;
    loadActivity();
    const t = setInterval(loadActivity, 15000);
    return () => clearInterval(t);
  }, [view, loadActivity]);

  const doRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.post(`/admin/team/${revokeTarget.id}/revoke`);
      toast.success(`${revokeTarget.name || revokeTarget.email} removed from team`);
      setRevokeTarget(null);
      load(search);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevoking(false);
    }
  };

  const filtered = members.filter((m) => {
    if (filter === 'admins') return m.is_admin;
    if (filter === 'interns') return !!m.intern_role;
    if (filter === 'staff') return m.is_staff && !m.intern_role && !m.is_admin;
    return true;
  });

  const roleBadge = (m) => {
    if (m.is_admin) return <Badge className="bg-purple-100 text-purple-700"><Crown className="w-3 h-3 mr-1" />Admin</Badge>;
    if (m.intern_role) return <Badge className="bg-emerald-100 text-emerald-700"><GraduationCap className="w-3 h-3 mr-1" />{m.intern_role_label || m.intern_role}</Badge>;
    if (m.is_staff) return <Badge className="bg-sky-100 text-sky-700"><Briefcase className="w-3 h-3 mr-1" />Staff</Badge>;
    return null;
  };

  const subscriptionBadge = (m) => {
    const plan = m.subscription_plan;
    if (plan === 'premium') {
      return <Badge className="bg-amber-100 text-amber-700"><Sparkles className="w-3 h-3 mr-1" />Premium comp</Badge>;
    }
    return <Badge variant="outline">{plan || 'free'}</Badge>;
  };

  return (
    <AdminChrome activeSection="team">
      <main className="p-4 md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="team-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin dashboard
            </Button>
            <h1 className="text-2xl font-bold text-slate-800 mt-2 flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-500" /> Team
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Everyone on Zazu&apos;s internal team — admins, staff, and interns. Regular users live under Users.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/employee/chat')} data-testid="team-open-chat">
            <MessageSquare className="w-4 h-4 mr-1" /> Team chat
          </Button>
        </div>

        {/* View tabs */}
        <div className="flex items-center gap-2 mb-4 border-b">
          <button
            onClick={() => setView('members')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === 'members' ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid="team-view-members"
          >
            <Users className="w-4 h-4 inline mr-1" /> Members
          </button>
          <button
            onClick={() => setView('activity')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === 'activity' ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid="team-view-activity"
          >
            <Activity className="w-4 h-4 inline mr-1" /> Activity
            {activity.online_count > 0 && (
              <Badge className="ml-2 bg-emerald-500 text-white text-[10px]" data-testid="team-online-badge">
                {activity.online_count} online
              </Badge>
            )}
          </button>
        </div>

        {view === 'activity' ? (
          <div data-testid="team-activity-view">
            {/* Online now */}
            <Card className="border-0 shadow-sm mb-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-slate-800 flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-emerald-500" /> Online now — last 5 minutes
                  </p>
                  <Button variant="ghost" size="sm" onClick={loadActivity} disabled={activityLoading} data-testid="activity-refresh">
                    {activityLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>
                {activity.online_now.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Nobody is on the platform right now.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {activity.online_now.map((u) => (
                      <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-md bg-emerald-50/50 border border-emerald-100" data-testid={`online-${u.user_id}`}>
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-semibold">
                            {initials(u.name || u.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800 truncate">{u.name}</p>
                          <p className="text-[11px] text-slate-500 truncate">{u.role_label} · {u.last_path}</p>
                        </div>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Activity feed */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <div className="p-4 border-b flex items-center justify-between">
                  <p className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4 text-slate-500" /> Activity feed — last 4 hours
                  </p>
                  <span className="text-xs text-slate-400">{activity.events.length} events</span>
                </div>
                {activity.events.length === 0 ? (
                  <p className="text-sm text-slate-400 italic text-center py-8">
                    No activity yet. Heartbeat fires every ~60s when a staff member is signed in.
                  </p>
                ) : (
                  <ul className="divide-y max-h-[60vh] overflow-y-auto">
                    {activity.events.map((e) => (
                      <li key={e.id} className="p-3 flex items-start gap-3 text-sm hover:bg-slate-50" data-testid={`activity-event-${e.id}`}>
                        <Avatar className="w-7 h-7">
                          <AvatarFallback className="bg-slate-200 text-slate-700 text-[10px]">
                            {initials(e.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-800">
                            <strong>{e.name}</strong>
                            <span className="text-slate-500"> · {e.role_label}</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{e.event}</Badge>
                            <code className="bg-slate-100 rounded px-1.5 py-0.5 text-[11px] max-w-[300px] truncate">{e.path}</code>
                          </p>
                        </div>
                        <span className="text-[11px] text-slate-400 flex items-center gap-1 flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          {(e.created_at || '').replace('T', ' ').slice(11, 16)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
        <>
        {/* Stat tiles — clickable filters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { key: 'all',     label: 'All',     count: stats.total,   icon: Users,          color: 'text-slate-600' },
            { key: 'admins',  label: 'Admins',  count: stats.admins,  icon: Crown,          color: 'text-purple-600' },
            { key: 'interns', label: 'Interns', count: stats.interns, icon: GraduationCap,  color: 'text-emerald-600' },
            { key: 'staff',   label: 'Staff',   count: stats.staff,   icon: Briefcase,      color: 'text-sky-600' },
          ].map((s) => {
            const active = filter === s.key;
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`text-left rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                  active ? 'ring-2 ring-emerald-500 shadow-md scale-[1.02]' : 'hover:shadow-md hover:scale-[1.01]'
                }`}
                data-testid={`team-tile-${s.key}`}
              >
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Icon className={`w-5 h-5 ${s.color}`} />
                      <span className="text-2xl font-bold text-slate-800">{s.count}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.label}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(search); }}
              className="pl-9"
              data-testid="team-search-input"
            />
          </div>
          <Button variant="outline" onClick={() => load(search)} data-testid="team-search-btn">Search</Button>
          {search && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); load(''); }} data-testid="team-search-clear">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Members list */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No team members {filter !== 'all' ? `in "${filter}"` : ''} yet.</p>
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() => navigate('/admin/careers')}
                  data-testid="team-goto-hiring"
                >
                  Hire someone from Careers →
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map((m) => (
                  <div key={m.id} className="p-4 flex items-center gap-4" data-testid={`team-member-${m.id}`}>
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm font-semibold">
                        {initials(m.name || m.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 truncate">{m.name || m.email.split('@')[0]}</p>
                        {roleBadge(m)}
                        {subscriptionBadge(m)}
                        {m.pending_daily_tasks > 0 && (
                          <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">
                            {m.pending_daily_tasks} open task{m.pending_daily_tasks === 1 ? '' : 's'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1 flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>
                        {m.hired_at && (
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />
                            Hired {m.hired_at.split('T')[0]}
                          </span>
                        )}
                        {m.start_date && (
                          <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" />
                            Starts {m.start_date}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${m.id}`)}
                        data-testid={`team-view-${m.id}`}
                      >
                        View
                      </Button>
                      {!m.is_admin && user?.is_admin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/employees/${m.id}/hr`)}
                          className="text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                          data-testid={`team-edit-hr-${m.id}`}
                          title="Edit HR information"
                        >
                          Edit HR
                        </Button>
                      )}
                      {!m.is_admin && user?.is_admin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await impersonateUser(m.id, false);
                              toast.success(`Now impersonating ${m.name || m.email}`);
                              // Small delay so the token/localStorage settle, then route to
                              // an area the impersonated user actually has access to.
                              setTimeout(() => {
                                if (m.intern_role) navigate('/intern/dashboard');
                                else if (m.is_staff) navigate('/employee/center');
                                else navigate('/dashboard');
                                window.location.reload();
                              }, 250);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Could not impersonate');
                            }
                          }}
                          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          data-testid={`team-impersonate-${m.id}`}
                          title="See the app as this user"
                        >
                          <UserCog className="w-4 h-4 mr-1" />
                          Impersonate
                        </Button>
                      )}
                      {!m.is_admin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRevokeTarget(m)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`team-revoke-${m.id}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}
      </main>

      {/* Revoke dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent className="max-w-md" data-testid="revoke-dialog">
          {revokeTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" /> Remove from team?
                </DialogTitle>
                <DialogDescription>
                  <strong>{revokeTarget.name || revokeTarget.email}</strong> will lose their staff/intern role and their comp&apos;d Premium access. Their account is not deleted — they become a regular Zazu user again.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRevokeTarget(null)}>Cancel</Button>
                <Button
                  onClick={doRevoke}
                  disabled={revoking}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="revoke-confirm-btn"
                >
                  {revoking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Revoke access
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminChrome>
  );
};

export default AdminTeam;
